import { useRef, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("kindred_token")}` });

export function useVoiceInput(onText) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const browserRecRef = useRef(null);

  const browserFallback = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return false; }
    const r = new SR();
    r.lang = "en-US";
    r.onresult = (e) => onText(e.results[0][0].transcript);
    r.onend = () => setRecording(false);
    r.onerror = () => { setRecording(false); toast.error("Couldn't hear you. Please try again."); };
    browserRecRef.current = r;
    r.start();
    setRecording(true);
    return true;
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return browserFallback();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 1000) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, mime.includes("mp4") ? "speech.mp4" : "speech.webm");
          const res = await fetch(`${API}/ai/transcribe`, { method: "POST", headers: authHeader(), body: fd });
          if (!res.ok) throw new Error();
          const { text } = await res.json();
          if (text) onText(text); else toast.error("I didn't catch that — please try again.");
        } catch {
          toast.error("Couldn't transcribe. Please try again.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access is needed to talk. Please allow it.");
    }
  };

  const stop = () => {
    if (recRef.current?.state === "recording") recRef.current.stop();
    browserRecRef.current?.stop();
    setRecording(false);
  };

  return { recording, transcribing, start, stop };
}

export function useSpeaker() {
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);

  const stop = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const speak = async (text) => {
    stop();
    if (!text) return;
    setSpeaking(true);
    try {
      const res = await fetch(`${API}/ai/speak`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeader() }, body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error();
      const { url } = await res.json();
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${url}`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      if (window.speechSynthesis) {
        const u = new SpeechSynthesisUtterance(text);
        u.rate = 0.95;
        u.onend = () => setSpeaking(false);
        window.speechSynthesis.speak(u);
      } else setSpeaking(false);
    }
  };

  return { speaking, speak, stop };
}
